/**
 * Shared job role family types for API contracts between frontend and backend
 */
import { z } from 'zod';
import { uuidSchema, ttocJobCodeSchema } from "./validation-common.js";
import { BreakResponseSchema } from './break.js';
import { SkillReferenceSchema, ScheduleSettingsSchema } from './jobRole.js';
export const ExpectedAttritionParamsSchema = z.object({
    numMonths: z.number().int().positive(), // Must be > 0
    expectedLeavers: z.number().int().nonnegative(), // Can be 0 or more (no upper limit)
    totalEmployees: z.number().int().min(1), // Must be >= 1
});
// JobRoleFamily response schema
export const JobRoleFamilyResponseSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    name: z.string(),
    jobCode: z.string().nullable(),
    ttocJobCode: z.string().nullable(),
    tipEligible: z.boolean(),
    color: z.string(),
    notes: z.string(),
    companyId: z.uuid(),
    expectedAttrition: z.number(),
    expectedAttritionParams: ExpectedAttritionParamsSchema,
    availableAtAllLocations: z.boolean(),
    isArchived: z.boolean(),
    skills: z.array(SkillReferenceSchema),
});
// JobRoleFamilyDetail response schema (extends base with relations)
export const JobRoleFamilyDetailResponseSchema = JobRoleFamilyResponseSchema.extend({
    locations: z.array(z.object({ id: z.uuid(), name: z.string() })),
    jobRoles: z.array(z.object({ id: z.uuid(), name: z.string() })),
    breaks: z.array(BreakResponseSchema),
    scheduleSettings: ScheduleSettingsSchema.nullable(),
});
// Input schemas
export const CreateJobRoleFamilyInputSchema = z.object({
    name: z.string(),
    companyId: uuidSchema,
    jobCode: z.string().optional(),
    ttocJobCode: ttocJobCodeSchema.optional(),
    tipEligible: z.boolean().default(false),
    color: z.string().default(""),
    notes: z.string().default(""),
    availableAtAllLocations: z.boolean().optional(),
    locationIds: z.array(uuidSchema).optional(),
    expectedAttritionParams: ExpectedAttritionParamsSchema,
    shift: z.object({
        min: z.number(),
        idealMin: z.number(),
        idealMax: z.number(),
        max: z.number(),
    }).optional(),
    weeklyHours: z.object({
        min: z.number(),
        idealMin: z.number(),
        idealMax: z.number(),
        max: z.number(),
    }).optional(),
}).refine((data) => !data.tipEligible || data.ttocJobCode !== undefined, {
    message: "ttocJobCode is required when tipEligible is true",
    path: ["ttocJobCode"],
});
export const UpdateJobRoleFamilyInputSchema = z.object({
    name: z.string().optional(),
    jobCode: z.string().nullable().optional(),
    ttocJobCode: ttocJobCodeSchema.nullable().optional(),
    tipEligible: z.boolean().optional(),
    color: z.string().optional(),
    notes: z.string().optional(),
    expectedAttritionParams: ExpectedAttritionParamsSchema.optional(),
    availableAtAllLocations: z.boolean().optional(),
    locationIds: z.array(uuidSchema).optional(),
    isArchived: z.boolean().optional(),
    shift: z.object({
        min: z.number(),
        idealMin: z.number(),
        idealMax: z.number(),
        max: z.number(),
    }).optional(),
    weeklyHours: z.object({
        min: z.number(),
        idealMin: z.number(),
        idealMax: z.number(),
        max: z.number(),
    }).optional(),
});
export const UpdateJobRoleFamilySkillsInputSchema = z.object({
    skillIds: z.array(uuidSchema),
});
export const AddJobRoleFamilySkillInputSchema = z.object({
    skillId: uuidSchema,
});
// Archive/unarchive input schema - empty because companyId comes from JWT context
// The id comes from the path parameter in the contract
export const ArchiveJobRoleFamilyInputSchema = z.object({});
export const JobRoleFamilyListQuerySchema = z.object({
    companyId: uuidSchema,
    includeArchived: z.string().optional().transform((val) => val === "true"),
});
